import { CheckIcon } from "@heroicons/react/24/outline";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as React from "react";
import { cn } from "@/core/utils/utils";

interface IconSelectContextType {
  value: string[];
  onValueChange: (value: string) => void;
  isOpen?: boolean;
}

const IconSelectContext = React.createContext<IconSelectContextType>({
  value: [],
  onValueChange: () => {},
});

interface IconSelectProps {
  value?: string[];
  onValueChange?: (value: string[]) => void;
  children: React.ReactNode;
  defaultValue?: string[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const IconSelect = ({
  value,
  onValueChange,
  children,
  defaultValue,
  open,
  onOpenChange,
}: IconSelectProps) => {
  const [internalValue, setInternalValue] = React.useState<string[]>(
    defaultValue || [],
  );

  const currentValue = value !== undefined ? value : internalValue;

  const handleValueChange = React.useCallback(
    (newValue: string) => {
      const updatedValue = currentValue.includes(newValue)
        ? currentValue.filter((v) => v !== newValue)
        : [...currentValue, newValue];

      if (onValueChange) {
        onValueChange(updatedValue);
      } else {
        setInternalValue(updatedValue);
      }
    },
    [currentValue, onValueChange],
  );

  const contextValue = React.useMemo(
    () => ({
      value: currentValue,
      onValueChange: handleValueChange,
      isOpen: open,
    }),
    [currentValue, handleValueChange, open],
  );

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <IconSelectContext.Provider value={contextValue}>
        {children}
      </IconSelectContext.Provider>
    </PopoverPrimitive.Root>
  );
};

interface IconSelectTriggerProps
  extends React.ComponentPropsWithoutRef<"button"> {
  children: React.ReactNode;
  isActive?: boolean;
}

const IconSelectTrigger = React.forwardRef<
  HTMLButtonElement,
  IconSelectTriggerProps
>(({ className, children, isActive = false, ...props }, ref) => {
  const context = React.useContext(IconSelectContext);

  return (
    <PopoverPrimitive.Trigger asChild>
      <button
        ref={ref}
        className={cn(
          "inline-flex h-10 w-10 min-w-10 items-center justify-center rounded-full bg-surface-primary text-primary-text transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring active:bg-surface-secondary disabled:pointer-events-none disabled:bg-surface-secondary disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0",
          {
            "bg-surface-secondary": context.isOpen,
            "bg-blue-5": !context.isOpen && isActive,
          },
          className,
        )}
        {...props}
      >
        {children}
      </button>
    </PopoverPrimitive.Trigger>
  );
});
IconSelectTrigger.displayName = "IconSelectTrigger";

interface IconSelectContentProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> {
  align?: "start" | "center" | "end";
}

const IconSelectContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  IconSelectContentProps
>(({ className, children, align, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      className={cn(
        "z-50 min-w-[12.5rem] overflow-hidden rounded-md border bg-popover p-0 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    >
      <div className="max-h-96 overflow-y-auto">{children}</div>
    </PopoverPrimitive.Content>
  </PopoverPrimitive.Portal>
));
IconSelectContent.displayName = "IconSelectContent";

interface IconSelectItemProps extends React.ComponentPropsWithoutRef<"button"> {
  value: string;
}

const IconSelectItem = React.forwardRef<HTMLButtonElement, IconSelectItemProps>(
  ({ className, children, value: itemValue, ...props }, ref) => {
    const context = React.useContext(IconSelectContext);
    const isSelected = context.value.includes(itemValue);

    return (
      <button
        ref={ref}
        onClick={() => context.onValueChange(itemValue)}
        className={cn(
          "relative flex w-full cursor-pointer select-none items-center justify-between gap-2 px-4 py-2 text-md font-normal outline-none hover:bg-surface-secondary data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          { "bg-blue-5": isSelected },
          className,
        )}
        {...props}
      >
        <span>{children}</span>
        <div className="flex size-5 items-center justify-center">
          <CheckIcon
            className={cn(
              "size-5 text-tertiary-text",
              !isSelected && "invisible",
            )}
          />
        </div>
      </button>
    );
  },
);
IconSelectItem.displayName = "IconSelectItem";

export { IconSelect, IconSelectTrigger, IconSelectContent, IconSelectItem };
